import { SoundByte, SoundType } from "../../enum/sounds";
import { Time } from "../../lib/Time";
import Transform from "../../lib/Transform";
import { Physics } from "../../math/Physics";
import Vector2 from "../../math/Vector2";
import { clamp, clamp01, lerp } from "../../util";
import Color from "../../util/Color";
import { Random } from "../../util/Random";
import { Shapes } from "../../util/Shapes";
import Explosion from "../Explosion";
import ExpOrb from "../ExpOrb";
import SoundManager from "../SoundManager";
import SPBogey from "../SPBogey";
import SPBullet from "../SPBullet";
import SPGameManager from "../SPGameManager";
import SPPlayer from "../SPPlayer";
import Diver from "./Diver";
import HomingBogey from "./HomingBogey";

export class HivePart extends SPBogey {
    parent: Hive|undefined;
    offset = {
        angle: 0,
        distance: 0
    }
    seed = Random.float(-3, 3);
    baseSize = 8;
    hp = 50;
    maxHP: number = 50;
    angleFromPlayer = 100;
    fireRate: number = 2;
    defenseTimer = 0;

    Start(): void {
        this.baseSize = this.size;
    }

    ActivateDefense() {
        this.defenseTimer = 2;
    }

    Damage(amt: number): void {
        if (!this.parent?.HasEggsRemaining()) {
            super.Damage(amt);
        }
    }

    Die(crashedIntoPlayer?: boolean): void {
        if (!this.parent) return;
        super.Die(crashedIntoPlayer);

        if (!this.parent.isDead) {
            this.parent.RemoveBodyPart(this.gameObject.id);
            this.parent.Damage(this.maxHP, {bypassExp: true});
        }
    }

    ShootAt(target: Transform) {
        if (this.fireRateTimer > 0 || this.fireRate === 0) return;

        this.fireRateTimer = 1/this.fireRate;
        const spawnPos = this.transform.position.add(this.transform.forward.scale(this.size + 3));
        const targetShotPos = SPPlayer.Singleton.transform.position.add(SPPlayer.Singleton.velocity)
        const vec = targetShotPos.sub(spawnPos);
        const distToShotPos = vec.magnitude;
        // vec.normalize();

        this.engine.NewGameObjectWithScript(SPBullet, {
            position: spawnPos,
            initialValues: {
                velocity:  Vector2.clampMagnitude(vec, 200),
                damage: 1,
                isPlayerBullet: false,
                owner: this
            }
        });
        SoundManager.Singleton.PlaySound(SoundByte.EnemyFireGun, SoundType.SFX, 0.4);
    }

    Update(): void {
        if (!this.parent) return;

        if (this.fireRateTimer > 0) {
            this.fireRateTimer -= Time.deltaTime;

            if (this.fireRateTimer <= 0) this.fireRateTimer = 0;
        }

        if (Vector2.distance(SPPlayer.Singleton.transform.position, this.transform.position) <= SPPlayer.Singleton.size + this.size) {
            SPPlayer.Singleton.Damage(1, {
                bypassExp: true,
                attacker: this
            });
        }

        const vec = this.parent.transform.forward.rotate(this.offset.angle).scale(this.offset.distance);
        this.transform.position = this.parent.transform.position.add(vec);

        this.size = Math.sin((Time.time+this.seed) * 4) * 2 + this.baseSize;

        this.angleFromPlayer = this.RotateTowardsPlayer(2*Math.PI);

        if (this.parent.bodyParts.filter(v => v instanceof HiveEgg).length === 0) {
            if (this.angleFromPlayer <= 0.2 && (Vector2.distance(this.transform.position, SPPlayer.Singleton.transform.position) <= this.parent.outerRadius + SPPlayer.Singleton.size || this.defenseTimer > 0)) {
                
                this.ShootAt(SPPlayer.Singleton.transform);
                
            }
        }

        if (this.defenseTimer > 0) {
            this.defenseTimer -= Time.deltaTime;

            if (this.defenseTimer < 0) this.defenseTimer = 0;
        }
    }

    Draw(): void {
        this.ctx.strokeStyle = 'white';
        this.ctx.lineWidth = 1;
        this.engine.StrokeCircle(this.transform.position, this.size);

        this.engine.Line(this.transform.position, this.transform.position.add(this.transform.forward.scale(this.size)));
    }

    DrawGizmos(): void {
        this.engine.drawTextsWorldPosition([`${this.angleFromPlayer}`], this.transform.position, {color: 'white'})
    }

}

export class HiveEgg extends HivePart {

    hp = 100;
    maxHP: number = 100;
    color = new Color(255,128,0);
    spawnTimer = Random.int(1, 10);

    Damage(amt: number) {
        if (this.isDead || this.isInvuln) return;

        this.hp -= amt;

        if (this.hp <= 0) {
            this.hp = 0;
            this.Die();
        }
    }

    Die(crashedIntoPlayer?: boolean): void {
        super.Die(crashedIntoPlayer);
        if (!this.parent) return;

        for (const part of this.parent.bodyParts.filter(v => v instanceof HivePart && !(v instanceof HiveEgg))) {
            if (Vector2.distance(part.transform.position, this.transform.position) <= this.size+part.size) {
                part.Die(crashedIntoPlayer);
            }
        }
    }

    Update(): void {
        super.Update();

        this.spawnTimer -= Time.deltaTime;
        if (this.spawnTimer <= 0) {
            this.spawnTimer = 10;
            this.engine.NewGameObjectWithScript<HomingBogey>(HomingBogey, {
                position: this.transform.position
            });
        }
    }

    Draw(): void {

        this.engine.GradientCircle(this.transform.position, this.size, {
            start: 'transparent',
            end: this.color.toHex()
        });
        this.ctx.strokeStyle = 'white';
        this.ctx.lineWidth = 1;
        this.engine.Line(this.transform.position, this.transform.position.add(this.transform.forward.scale(this.size)));
    }

}

enum HiveState {
    Stage1,
    Stage2,
    Laser,
    Travelling
}

export default class Hive extends SPBogey {
    innerRadius = 32;
    outerRadius = 96;
    size: number = this.innerRadius/2;
    hp = 100;
    maxHP = 100;
    name = "Hiveship"
    laserTimer = 5;
    travelDest: Vector2|undefined;
    
    _state: HiveState = HiveState.Stage1;

    bodyParts: HivePart[] = [];

    HasEggsRemaining() {
        return this.bodyParts.filter(v => v instanceof HiveEgg).length > 0;
    }

    RemoveBodyPart(id: string) {
        this.bodyParts = this.bodyParts.filter(v => v.gameObject.id !== id);
    }

    Damage(amt: number, options?: { bypassExp?: boolean; attacker?: SPBogey | SPPlayer; }): void {
        if (!this.HasEggsRemaining() || options?.bypassExp) {
            super.Damage(amt);
            if (this._state !== HiveState.Stage1) this.bodyParts.forEach(part => part.ActivateDefense());
        }
    }

    Die(crashedIntoPlayer?: boolean): void {
        super.Die(crashedIntoPlayer);

        for (const part of this.bodyParts) {
            part.Die();
        }
    }

    Start(): void {
        for (let ang = 60; ang <= 300; ang += 240/8) {
            const theta = ang * Math.PI / 180;
            const [,part] = this.engine.NewGameObjectWithScript<HivePart>(HivePart, {
                initialValues: {
                    parent: this,
                    offset: {
                        angle: theta,
                        distance: this.innerRadius
                    }
                }
            });
            this.bodyParts.push(part);
        }

        for (let ang = 60; ang <= 300; ang += 240/4) {
            const theta = ang * Math.PI / 180;
            const [,part] = this.engine.NewGameObjectWithScript<HiveEgg>(HiveEgg, {
                initialValues: {
                    parent: this,
                    offset: {
                        angle: theta,
                        distance: this.outerRadius,
                    },
                    size: this.innerRadius
                }
            });
            this.bodyParts.push(part);

            let d = this.outerRadius;

            while (d > this.innerRadius) {
                const [,arm] = this.engine.NewGameObjectWithScript<HivePart>(HivePart, {
                    initialValues: {
                        parent: this,
                        offset: {
                            angle: theta,
                            distance: d
                        }
                    }
                });

                this.bodyParts.push(arm);
                d -= 16;
            }
        }

        this.maxHP = this.bodyParts.map(v => v.maxHP).reduce((a,b) => a+b);
        this.hp = this.maxHP;
        console.log(this.maxHP);
    }

    laserChargeTimer = 5.14;
    laserChargeSize = 256;
    laserBeamTimer = 7.04;
    particles: Vector2[] = [];
    LaserUpdate() {
        if (this.laserChargeTimer > 0) {
            this.laserChargeTimer -= Time.deltaTime;

            const chargeT = clamp01(this.laserChargeTimer/5.14);
            this.particles.push(this.transform.position.add(Vector2.fromRadians(Random.float(0, 2*Math.PI)).scale(Random.float(0, chargeT * this.laserChargeSize))));

            for (let i = this.particles.length-1; i>-1; i--) {
                this.particles[i] = this.particles[i].add(this.transform.position.sub(this.particles[i]).normalized.scale(128*Time.deltaTime));
                if (Vector2.distance(this.particles[i], this.transform.position) <= 16) this.particles.splice(i, 1);
            }

            if (this.laserChargeTimer <= 0) {
                this.laserChargeTimer = 0;
                SoundManager.Singleton.PlaySound(SoundByte.LaserBeam);
            }

            this.RotateTowardsPlayer(2*Math.PI);
        } else {
            //check if in laser
            const chargeT = clamp01(this.laserChargeTimer/5.14);
            if (Physics.CircleLineCollision({
                position: SPPlayer.Singleton.transform.position,
                radius: SPPlayer.Singleton.size
            }, {
                start: this.transform.position,
                end: this.transform.position.add(this.transform.forward.scale(1024))
            }, 32*chargeT)) {
                SPPlayer.Singleton.Damage(1);
            }

            this.laserBeamTimer -= Time.deltaTime;
            this.RotateTowardsPlayer(Math.PI/2);

            if (this.laserBeamTimer <= 0) {
                this.laserBeamTimer = 0;
                this._state = HiveState.Stage2;
                this.laserTimer = Random.int(5, 15, true);
            }
        }
    }
    Stage1Update() {
        this.RotateTowardsPlayer(Math.PI/90);

        if (!this.HasEggsRemaining()) this._state = HiveState.Stage2;
    }
    Stage2Update() {
        this.laserTimer -= Time.deltaTime;

        this.RotateTowardsPlayer(Math.PI/8);

        if (this.laserTimer <= 0) {
            this.laserTimer = 0;
            this._state = HiveState.Laser;
            this.laserChargeTimer = 5.14;
            this.laserBeamTimer = 7.04;
            this.laserChargeSize = 256;
            SoundManager.Singleton.PlaySound(SoundByte.LaserCharge);
        }
    }
    TravellingUpdate() {
        if (!this.travelDest) return;

        this.velocity = this.travelDest.sub(this.transform.position).normalized.scale(128);

        if (Vector2.distance(this.transform.position, this.travelDest) <= 16) {
            this.velocity = Vector2.zero;
            this._state = HiveState.Stage1;
        }

        this.transform.position = this.transform.position.add(this.velocity.scale(Time.deltaTime));
    }

    Update(): void {

        if (!SPGameManager.Singleton.isInGame && !SPGameManager.Singleton.isInGameOrGameOver) return;

        switch (this._state) {
            case HiveState.Laser:
                this.LaserUpdate();
                break;
            case HiveState.Stage1:
                this.Stage1Update();
                break;
            case HiveState.Stage2:
                this.Stage2Update();
                break;
            case HiveState.Travelling:
                this.TravellingUpdate();
                break;
        
            default:
                break;
        }

        
    }

    Draw(): void {
        // this.engine.StrokeCircle(this.transform.position, this.outerRadius);
        // this.engine.Line(this.transform.position, this.transform.position.add(this.transform.forward.scale(this.innerRadius)));

        this.ctx.strokeStyle = 'white';
        this.ctx.lineWidth = 1;
        for (const egg of this.bodyParts.filter(v => v instanceof HiveEgg)) {
            this.engine.Line(this.transform.position, egg.transform.position);
        }

        this.ctx.strokeStyle = 'orange';
        this.ctx.lineWidth = 2;
        this.engine.StrokeCircle(this.transform.position, this.innerRadius/2);

        if (this.HasEggsRemaining()) {
            this.engine.GradientCircle(this.transform.position, this.innerRadius/2, {
                start: "#ff800055",
                end: "#ff8000"
            });
        }


        if (this._state === HiveState.Laser) {

            for (const particle of this.particles) {
                this.ctx.fillStyle = '#ffffff30';
                this.engine.FillCircle(particle, 2);
            }

            const chargeT = clamp01(this.laserChargeTimer/5.14);
            this.ctx.lineWidth = 1;
            this.ctx.strokeStyle = Color.fromHex("#ffffff").toHexWithAlpha(1 - chargeT);
            this.engine.StrokeCircle(this.transform.position, this.laserChargeSize * chargeT);
            this.ctx.fillStyle = 'white';
            this.engine.FillCircle(this.transform.position, 16 * (1-chargeT));
            
            if (this.laserChargeTimer === 0) {
                const bak = this.ctx.lineCap;
                const t = clamp01(this.laserBeamTimer/7.04);
                this.ctx.lineCap = 'round';
                this.ctx.lineWidth = 32*t;
                this.engine.Line(this.transform.position, this.transform.position.add(this.transform.forward.scale(1024)));
                this.ctx.lineCap = bak;
            }
        }


        if (this.isBoss) this.DrawBossBar();
    }

}